import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message, Modal, Spin, Switch } from 'antd';
import dayjs from 'dayjs';
import type { IAdBudgetDisplay, IPracticePoolBudget, SpecialtyOverrides } from 'types/marketing';
import { BudgetPeriod } from 'types/marketing';
import { buildPracticeSaveRequest, isProviderListed, poolBudgetToDisplay } from 'utils/marketing';
import usePracticeByProvider from 'hooks/marketing/usePracticeByProvider';
import { saveGPAdPoolBudget } from 'api/operation/group';
import StrategyTable from 'pages/ProListingSetup/components/StrategyTable';
import GroupBudgetDistribution from 'pages/dashboard/Marketing/components/GroupBudgetDistribution';
import GroupSpecialtyOverrides from 'pages/dashboard/Marketing/components/GroupSpecialtyOverrides';
import s from './s.module.less';

const DefaultSpecialtyBid = 60;

interface IProps {
    visible: boolean;
    organizationId: number;
    poolBudget?: IPracticePoolBudget;
    stdListingCount: number;
    poolExhausted: boolean;
    hasJoinedProListing: boolean;
    remainingBudget?: number;
    lastModifiedAt?: string | null;
    lastModifiedBy?: string | null;
    onCancel: () => void;
    onSaved?: () => void;
}

function getDateRange(weeks: number) {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - weeks * 7);
    return {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
    };
}

// Ops "Edit campaign" modal for a group practice pool campaign. Mirrors the provider
// GroupCampaignSetup editing surface (strategy + budget distribution + specialty overrides),
// but saves directly through the OPS org-scoped endpoint with no consumer confirm/agreement step.
// Doubles as first-time setup when the practice has no campaign yet: the same endpoint
// (ad/budgets/saveOrUpdate) creates the pool, so we only swap the wording and the defaults.
const GroupProlistingModal: React.FC<IProps> = ({
    visible,
    organizationId,
    poolBudget,
    stdListingCount,
    poolExhausted,
    hasJoinedProListing,
    remainingBudget,
    lastModifiedAt,
    lastModifiedBy,
    onCancel,
    onSaved,
}) => {
    const [providerRows, providerRowsLoading] = usePracticeByProvider(getDateRange(4), true, organizationId);

    // No campaign yet: feed poolBudgetToDisplay an empty shell so it falls back to
    // its own defaults (WEEKLY / $300 / $60 bid), same as the provider-side pre-launch form.
    const isPreLaunch = !hasJoinedProListing;
    const effectivePoolBudget: IPracticePoolBudget = useMemo(
        () =>
            poolBudget || {
                organizationId,
                defaultBidAmountCents: 0,
                pool: null,
                specialtyBids: [],
                members: [],
            },
        [poolBudget, organizationId],
    );

    const [strategyData, setStrategyData] = useState<IAdBudgetDisplay>({
        budgetPeriod: BudgetPeriod.WEEKLY,
        budgetCap: 300,
        generalBidAmount: DefaultSpecialtyBid,
        enableAdvancedSettings: false,
    });
    const [advancedSettings, setAdvancedSettings] = useState(false);
    const [budgetExcluded, setBudgetExcluded] = useState<Set<number>>(new Set());
    const [initialOverrides, setInitialOverrides] = useState<SpecialtyOverrides>();
    const [specialtyOverrides, setSpecialtyOverrides] = useState<SpecialtyOverrides>();
    const [strategyTableErrors, setStrategyTableErrors] = useState({ bidError: '', budgetError: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const { strategyData: display, initialOverrides: overrides } = poolBudgetToDisplay(effectivePoolBudget);
        // status comes from poolBudgetToDisplay; last-modified is overview-only so injected here.
        setStrategyData({
            ...display,
            remainingBudget,
            updatedAt: lastModifiedAt ? dayjs(lastModifiedAt).format('MM/DD/YYYY') : undefined,
            updatedByName: lastModifiedBy || undefined,
        });
        setAdvancedSettings(display.enableAdvancedSettings || false);
        setInitialOverrides(overrides);
    }, [effectivePoolBudget, remainingBudget, lastModifiedAt, lastModifiedBy]);

    // Providers not on Pro-Listing (or unlisted) start excluded from the split.
    // Pre-launch there is no onProListing flag yet, so every listed provider defaults to on.
    useEffect(() => {
        if (providerRows.length === 0) return;
        const notListed = providerRows.filter((r) => !isProviderListed(r)).map((r) => r.providerId);
        if (isPreLaunch) {
            setBudgetExcluded(new Set(notListed));
            return;
        }
        setBudgetExcluded(
            new Set(providerRows.filter((r) => !r.onProListing || !isProviderListed(r)).map((r) => r.providerId)),
        );
    }, [providerRows, isPreLaunch]);

    // A provider whose every specialty is disabled is auto-excluded from the budget split.
    useEffect(() => {
        if (!specialtyOverrides) return;
        const { disabledSpecs, disabledProvSpecs } = specialtyOverrides;
        setBudgetExcluded((prev) => {
            let changed = false;
            const next = new Set(prev);
            providerRows.forEach((p) => {
                if (prev.has(p.providerId)) return;
                const specKeys = p.services
                    ?.filter((svc) => svc.specialtyName)
                    .map((svc) => svc.specialtyName.toLowerCase().replace(/\s+/g, '_'));
                if (!specKeys?.length) return;
                const allDisabled = specKeys.every(
                    (k) => disabledSpecs.has(k) || disabledProvSpecs[k]?.has(p.providerId),
                );
                if (allDisabled) {
                    next.add(p.providerId);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [specialtyOverrides, providerRows]);

    const toggleBudgetProvider = (id: number) => {
        setBudgetExcluded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const activeProviderCount = useMemo(
        () => providerRows.filter((r) => !budgetExcluded.has(r.providerId)).length,
        [providerRows, budgetExcluded],
    );

    const budgetSummary = useMemo(() => {
        const cap = strategyData.budgetCap || 0;
        const count = activeProviderCount || 0;
        const perProvider = count > 0 ? Math.round(cap / count) : 0;
        const periodLabel = strategyData.budgetPeriod === 'MONTHLY' ? 'mo' : 'wk';
        return { cap, providerCount: count, perProvider, periodLabel };
    }, [strategyData.budgetCap, strategyData.budgetPeriod, activeProviderCount]);

    const handleOverridesChange = useCallback((o: SpecialtyOverrides) => setSpecialtyOverrides(o), []);

    const changeAdvancedSettings = (checked: boolean) => {
        setAdvancedSettings(checked);
        setStrategyData((prev) => ({ ...prev, enableAdvancedSettings: checked }));
    };

    const handleSave = async () => {
        if (strategyTableErrors.bidError || strategyTableErrors.budgetError) return;
        setSaving(true);
        try {
            const req = buildPracticeSaveRequest(strategyData, providerRows, budgetExcluded, specialtyOverrides);
            const result = await saveGPAdPoolBudget(organizationId, { ...req, updateReason: '' });
            if (!result.error) {
                message.success(isPreLaunch ? 'Campaign launched' : 'Saved successfully');
                onSaved?.();
                onCancel();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={isPreLaunch ? 'Set up campaign' : 'Edit campaign'}
            open={visible}
            onCancel={onCancel}
            width={1036}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button key="save" type="primary" loading={saving} onClick={handleSave}>
                    {isPreLaunch ? 'Launch campaign' : 'Save'}
                </Button>,
            ]}
        >
            <div className={s.modalContent}>
                <StrategyTable
                    data={strategyData}
                    onChange={(d) => setStrategyData((prev) => ({ ...prev, ...d }))}
                    onValidationChange={setStrategyTableErrors}
                    isInGroup
                    isOps
                />

                <div className="mt-4">
                    <div className="flex items-center justify-between">
                        <div className="font-medium">Advanced settings</div>
                        <Switch checked={advancedSettings} onChange={changeAdvancedSettings} />
                    </div>
                    {advancedSettings && (
                        <Spin spinning={providerRowsLoading}>
                            <div className="mt-4 flex gap-4 flex-col">
                                <GroupBudgetDistribution
                                    providers={providerRows}
                                    budgetExcluded={budgetExcluded}
                                    budgetSummary={budgetSummary}
                                    stdListingCount={stdListingCount}
                                    hasJoinedProListing={hasJoinedProListing}
                                    activeProviderCount={activeProviderCount}
                                    poolExhausted={poolExhausted}
                                    onToggleProvider={toggleBudgetProvider}
                                />

                                <GroupSpecialtyOverrides
                                    providers={providerRows}
                                    baseBid={strategyData.generalBidAmount}
                                    budgetCap={strategyData.budgetCap || 0}
                                    budgetPeriod={strategyData.budgetPeriod}
                                    budgetExcluded={budgetExcluded}
                                    initialOverrides={initialOverrides}
                                    onChange={handleOverridesChange}
                                />
                            </div>
                        </Spin>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default GroupProlistingModal;
